import React, { useMemo, useState } from 'react'
import {
  DONUT_CHART_DEFAULT_TITLE,
  DONUT_CHART_DEFAULT_VALUE,
  DonutChart,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './DonutChartShowcase.css'

const SAMPLE_DONUTS = [
  { title: 'Collection', value: 83 },
  { title: 'Show rate', value: 91 },
  { title: 'Goal', value: 67 },
]

const DonutChartShowcase: React.FC = () => {
  const [value, setValue] = useState(DONUT_CHART_DEFAULT_VALUE)
  const [title, setTitle] = useState(DONUT_CHART_DEFAULT_TITLE)
  const [darkMode, setDarkMode] = useState(false)

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'donut-chart-value',
            label: 'Value',
            control: (
              <input
                id="donut-chart-value"
                className="ui-field-control"
                type="range"
                min={0}
                max={100}
                step={1}
                value={value}
                onChange={(event) => setValue(Number(event.target.value))}
              />
            ),
          },
          {
            id: 'donut-chart-title',
            label: 'Title',
            control: (
              <input
                id="donut-chart-title"
                className="ui-field-control"
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            ),
          },
          {
            id: 'donut-chart-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="donut-chart-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [darkMode, title, value],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Donut chart"
        description="Progress ring with rounded caps, blue-300 track, blue-500 arc, and centered title + value."
        stageClassName="ui-preview__stage--wide donut-chart-showcase__stage-wrap"
      >
        <div
          className={`donut-chart-showcase__sheet${
            darkMode ? ' donut-chart-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Sample KPIs · editable control below uses “{title}” at{' '}
              {Math.round(value)}%
            </p>
            <div className="donut-chart-showcase__live-row donut-chart-showcase__live-row--sample">
              {SAMPLE_DONUTS.map((item) => (
                <DonutChart
                  key={item.title}
                  title={item.title}
                  value={item.value}
                  size={200}
                  darkMode={darkMode}
                />
              ))}
            </div>
            <div className="donut-chart-showcase__live-control">
              <p className="donut-chart-showcase__live-control-label">
                Interactive
              </p>
              <DonutChart value={value} title={title} darkMode={darkMode} />
            </div>
          </section>

          <section className="donut-chart-showcase__matrix">
            <h4 className="donut-chart-showcase__section-title">States</h4>
            {[
              { label: 'Empty', previewValue: 0 },
              { label: 'Default (83%)', previewValue: 83 },
              { label: 'Complete', previewValue: 100 },
            ].map((state) => (
              <div
                key={state.label}
                className={`donut-chart-showcase__row${
                  state.previewValue === value
                    ? ' donut-chart-showcase__row--selected'
                    : ''
                }${darkMode ? ' donut-chart-showcase__row--dark' : ''}`}
              >
                <span className="donut-chart-showcase__row-label">
                  {state.label}
                </span>
                <div className="donut-chart-showcase__row-preview">
                  <DonutChart
                    value={state.previewValue}
                    title={title}
                    darkMode={darkMode}
                  />
                </div>
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Donut chart"
        sections={propertySections}
      />
    </>
  )
}

export default DonutChartShowcase
